import React, { useState } from "react";
import { Plus, Trash2, Search, Tag, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useProductVariations,
  useAddProductVariation,
  useDeleteProductVariation,
} from "@/hooks/use-product-variations";
import { ProductWithVariations } from "@/services/products/productVariationService";

export function ProductVariationsTab() {
  const { data: products, isLoading, error } = useProductVariations();
  const addVariation = useAddProductVariation();
  const deleteVariation = useDeleteProductVariation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [newVariations, setNewVariations] = useState<Record<string, string>>({});

  const toggleExpanded = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleAddVariation = (productId: string) => {
    const variationName = newVariations[productId]?.trim();
    if (!variationName) return;

    addVariation.mutate(
      { productId, variationName },
      {
        onSuccess: () => {
          setNewVariations((prev) => ({ ...prev, [productId]: "" }));
        },
      }
    );
  };

  const filteredProducts = (products || []).filter((product) => {
    const term = searchTerm.toLowerCase();
    if (product.name.toLowerCase().includes(term)) return true;
    return product.variations.some((v) =>
      v.variation_name.toLowerCase().includes(term)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Error loading variations: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products or variations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredProducts.length} products
        </div>
      </div>

      <div className="space-y-2">
        {filteredProducts.map((product) => (
          <ProductVariationCard
            key={product.id}
            product={product}
            isExpanded={expandedProducts.has(product.id)}
            onToggle={() => toggleExpanded(product.id)}
            newVariation={newVariations[product.id] || ""}
            onNewVariationChange={(value) =>
              setNewVariations((prev) => ({ ...prev, [product.id]: value }))
            }
            onAddVariation={() => handleAddVariation(product.id)}
            onDeleteVariation={(variationId) =>
              deleteVariation.mutate(variationId)
            }
            isAdding={addVariation.isPending}
            isDeleting={deleteVariation.isPending}
          />
        ))}

        {filteredProducts.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No products found matching "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  );
}

interface ProductVariationCardProps {
  product: ProductWithVariations;
  isExpanded: boolean;
  onToggle: () => void;
  newVariation: string;
  onNewVariationChange: (value: string) => void;
  onAddVariation: () => void;
  onDeleteVariation: (variationId: string) => void;
  isAdding: boolean;
  isDeleting: boolean;
}

function ProductVariationCard({
  product,
  isExpanded,
  onToggle,
  newVariation,
  onNewVariationChange,
  onAddVariation,
  onDeleteVariation,
  isAdding,
  isDeleting,
}: ProductVariationCardProps) {
  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <CardTitle className="text-base font-medium">
                  {product.name}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-normal">
                {product.variations.length} variation
                {product.variations.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Existing variations */}
            {product.variations.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.variations.map((variation) => (
                  <Badge
                    key={variation.id}
                    variant="outline"
                    className="flex items-center gap-1 py-1.5 pl-2 pr-1"
                  >
                    <Tag className="h-3 w-3 text-muted-foreground" />
                    <span>{variation.variation_name}</span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 ml-1 hover:bg-destructive/10 hover:text-destructive"
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Variation</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the variation "
                            {variation.variation_name}"? This action cannot be
                            undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => onDeleteVariation(variation.id)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </Badge>
                ))}
              </div>
            )}

            {/* Add new variation */}
            <div className="flex gap-2">
              <Input
                placeholder="Add new variation name..."
                value={newVariation}
                onChange={(e) => onNewVariationChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newVariation.trim()) {
                    onAddVariation();
                  }
                }}
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={onAddVariation}
                disabled={!newVariation.trim() || isAdding}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default ProductVariationsTab;
